import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { MasteryStatus, SkillType } from "@/enums";
import { AsyncLoadingState, WidgetCategory, WidgetType } from "../enums";
import { isBefore, startOfDay } from "date-fns";
import { useWeeklyPlan, useRosterInfo, usePlannerCompare } from "@/hooks";
import { getUserData } from "@/services/API";

// District-based IRIP generation logic
const TEMP_LINK_DISTRICT_ID = 1000000124; // District ID as number
const TEMPORARY_IRIPS_LINK = "https://docs.google.com/document/d/1c5uYR2dlGYaBR7jXHRcAl8JtrSLraPQ1";

// Store
import { useDispatch, useSelector } from "react-redux";
import {
  dashboardWidgetState,
  fetchStudentSkillsMastery,
  selectWeeklyPlanIsLoading,
  toggleWidget,
  WidgetStateEnum,
} from "@/store/slices";

// Components
import { WidgetWrapper } from "./WidgetWrapper";
import MiniLessonCard from "./MiniLessonCard/MiniLessonCard";
import IndividualLearningPath from "./IndividualLearningPath/IndividualLearningPath";
import { CurriculumAlignment } from "@/features/planner/components";
import { PlanningSummary } from "@/features/dashboard/components";

// Resources and assets
import "./WeeklyPlanContainer.scss";
import { ArrowIcon, DownloadIcon } from "@/components/Global";
import plannerIcon from "@/images/icons/widget-instruct.svg";
import alertIcon from "@/images/icons/alert.svg";
import WidgetDataReloadButton from "./WidgetDataReloadButton";

interface WeeklyPlanContainerProps {
  modeOne: boolean;
  modeTwo: boolean;
}

// A flag to enable/disable the planner compare feature
const PLANNER_COMPARE = false;

export const WeeklyPlanContainer: React.FC<WeeklyPlanContainerProps> = ({ 
  modeOne,
  modeTwo
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("dashboard");
  const { data: weeklyPlanData, skillsMasteryLoaded, skillsMasteryLoading, failedToLoad, reloadData } = useWeeklyPlan();
  const loading = useSelector(selectWeeklyPlanIsLoading);
  const { curriculum, curTrack } = weeklyPlanData ?? {};
  const { instruct } = useSelector(dashboardWidgetState);
  const { students } = useRosterInfo();
  const { isComparable, hasRollover, ...planningSummary } = usePlannerCompare();

  // IRIP generation state
  const [isGeneratingIRIPs, setIsGeneratingIRIPs] = useState(false);
  const [iripProgress, setIripProgress] = useState(0);

  // Check if this district should use temp link or real generation
  const shouldUseTempLink = useMemo(() => {
    try {
      const userData = getUserData();
      const currentDistrictId = userData?.districtId;
      
      // Handle both string and number comparisons
      return currentDistrictId === TEMP_LINK_DISTRICT_ID || 
             currentDistrictId === String(TEMP_LINK_DISTRICT_ID);
    } catch (error) {
      // If we can't get user data, default to real generation
      return false;
    }
  }, []);

  const lessons = useMemo(() => (curriculum?.tracks && curTrack?.label
    ? curriculum.tracks.find((t) => t.name === curTrack?.label)
      : null
  ),
  [ curriculum?.tracks, curTrack?.label]
  );
  const hasLessonUnits = useMemo(() => lessons && lessons.units.length > 0, [lessons]);
  const showRolloverComparison = useMemo(() => {
    return PLANNER_COMPARE && isComparable && !hasRollover;
  }, [isComparable, hasRollover]);

  useEffect(() => {
    if (
      !skillsMasteryLoaded &&
      students &&
      students.length &&
      curriculum?.tracks.length &&
      !Object.keys(skillsMasteryLoading).length
    ) {
      for (const track of curriculum.tracks) {
        for (const unit of track.units) {
          for (const lesson of unit.lessons) {
            if (isBefore(startOfDay(new Date(lesson.dateFrom)), startOfDay(new Date()))) {
              const lessonDate = lesson.dateFrom;
              dispatch(
                fetchStudentSkillsMastery({
                  studentId: students.map((s: { id: string }) => s.id),
                  skillId: lesson.skills.filter(s => s.skillType === SkillType.FOCUS).map(s => `${s.skillId}`),
                  lessonStartDate: lessonDate.split(' ')[0], // strange bug date strings sometimes have +' 00:00:00'
                  lessonPlanId: lesson.lessonPlanId,
                }) as any
              );
            }
          }
        }
      }
    }
  }, [curriculum, students, skillsMasteryLoaded, skillsMasteryLoading]);

  const alignment = useMemo(() => {
    if (!curriculum?.name) return null;
    return <CurriculumAlignment curriculumName={curriculum.name} />
  }, [curriculum?.name])

  // Listen for IRIP generation progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: any) => {
      const { isGenerating, progress } = event.detail;
      setIsGeneratingIRIPs(isGenerating);
      setIripProgress(progress || 0);
    };

    window.addEventListener('iripProgress', handleProgressUpdate);
    return () => window.removeEventListener('iripProgress', handleProgressUpdate);
  }, []);

  // Simple IRIP generation trigger - dispatches event to existing IRIPDocumentGenerator
  const handleGenerateIRIPs = () => {
    if (isGeneratingIRIPs) return; // Prevent multiple clicks
    
    // Pass students data through the event since IRIPDocumentGenerator might not have access to it on dashboard
    window.dispatchEvent(new CustomEvent('generateIRIPs', { 
      detail: { 
        students: students,
        weeklyPlan: weeklyPlanData 
      } 
    }));
  };

  return (
    <WidgetWrapper
      title=''
      overriddenTitle={
        modeTwo ? "INSTRUCT Weekly Plan" : "INSTRUCT Learning Paths"
      }
      type={WidgetType.WEEKLY_PLAN}
      category={WidgetCategory.INSTRUCT}
      icon={plannerIcon}
      isCollapsed={instruct.instructional === WidgetStateEnum.COLLAPSED}
      onCollapserHandler={() =>
        dispatch(toggleWidget({ group: "instruct", widget: "instructional" }))
      }
      loadingState={loading ? AsyncLoadingState.LOADING : AsyncLoadingState.NONE}
      dataTestId="weekly-plan-widget"
      alignment={alignment}
    >
      <hr className="widget-divider" />

      <div className="p-3">
      {
        modeOne ? (
          <IndividualLearningPath />
        ) :
        modeTwo ? (
          <>
            {failedToLoad && (
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 widget-body"
                style={{ height: "14rem" }}>
                <div className="data-failed-text mb-3 font-weight-bold">
                  Data failed to load
                </div>
                <WidgetDataReloadButton onReload={reloadData} />
              </div>
            )}
            { showRolloverComparison && !failedToLoad ? (
              <>
                <PlanningSummary {...planningSummary} />
                <footer className="d-flex w-100 justify-content-between mt-4 manage-instruct-link">
                  <NavLink className="btn btn-link ms-auto" to="/planner/rollover">
                    <span>{t("DIFFERENTIATE_INSTRUCTION")}</span>
                    <i className="icon"><ArrowIcon /></i>
                  </NavLink>
                </footer>
              </>
            ) : hasLessonUnits && !failedToLoad ? (
              <>
                <div className="d-flex gap-3 mini-lesson-card-bg">
                  {lessons?.units.map((unit) => {
                    return unit.lessons.map(
                      (
                        {
                          name: lessonName,
                          completionPercentage: percentComplete,
                          dateFrom,
                          masteryData,
                        },
                        index
                      ) => (
                        <MiniLessonCard
                          key={`${unit.name}-${lessonName}-${index}`}
                          date={new Date(dateFrom)}
                          percentComplete={percentComplete}
                          lessonName={lessonName}
                          unitName={unit.name}
                          mastery={
                            masteryData && masteryData.length > 1
                              ? // TODAY mastery data always on index 1
                                masteryData[1].data.reduce(
                                  (acc, cur) => {
                                    if (cur.value > acc.value) {
                                      acc = {
                                        label:
                                          cur.label === "mastered"
                                            ? MasteryStatus.LIKELY_MASTERED
                                            : cur.label === "developing"
                                              ? MasteryStatus.DEVELOPING
                                              : cur.label === "undeveloped"
                                                ? MasteryStatus.NOT_DEVELOPED
                                                : MasteryStatus.NO_DATA,
                                        value: cur.value,
                                      };
                                    }
                                    return acc;
                                  },
                                  { label: MasteryStatus.NO_DATA, value: 0 } as {
                                    label: MasteryStatus;
                                    value: number;
                                  }
                                ).label
                              : MasteryStatus.NO_DATA
                          }
                        />
                      )
                    );
                  })}
                </div>
                <footer className="d-flex w-100 justify-content-between mt-4 manage-instruct-link">
                  {shouldUseTempLink ? (
                    <a href={TEMPORARY_IRIPS_LINK} className="btn btn-link" target="_blank" rel="noopener noreferrer">
                      <span>{t("GENERATE_IRIPS", {count: 0})}</span>
                      <i className="icon"><DownloadIcon /></i>
                    </a>
                  ) : (
                    <button 
                      className="btn btn-link" 
                      onClick={handleGenerateIRIPs}
                      disabled={isGeneratingIRIPs}
                      style={{ 
                        opacity: isGeneratingIRIPs ? 0.7 : 1,
                        cursor: isGeneratingIRIPs ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span>
                        {isGeneratingIRIPs 
                          ? `Generating IRIPs... ${iripProgress}%` 
                          : t("GENERATE_IRIPS", {count: 0})
                        }
                      </span>
                      <i className="icon">
                        <DownloadIcon />
                      </i>
                    </button>
                  )}
                  <NavLink className="btn btn-link ms-auto" to="/planner">
                    <span>{t("MANAGE_PLAN")}</span>
                    <i className="icon"><ArrowIcon /></i>
                  </NavLink>
                </footer>
              </>
            ) : (
              <div className="d-flex gap-1 justify-content-center align-items-center mb-5 mt-4 instruct-plan-alert">
                <img
                  src={alertIcon}
                  alt={
                    t("WIDGET_NOTICE_WEEKLY_PLAN_EMPTY") ??
                    "empty instructional plan"
                  }
                />
                {t("WIDGET_NOTICE_WEEKLY_PLAN_EMPTY")}
              </div>
            )}
          </>
        ) : ''}
      </div>
    </WidgetWrapper>
  );
};

export default WeeklyPlanContainer;
